import debounce from 'lodash/debounce';
import { useMessageCenter } from '@/message/useMessageCenter';
import { bigDataLogOnRouteSwitch, useBigDataLog } from './useBigDataLog';
import bigDataLogDirective from '@/bigData/directives';

let memoFlatResourceResult;

/// 压扁 flat 用户的菜单/按钮资源数据 (使用记忆函数优化性能)
function flatResources(resources) {
    if (!resources || !resources.length) {
        return [];
    }

    if (memoFlatResourceResult) {
        return memoFlatResourceResult;
    }

    const cloned = JSON.parse(JSON.stringify(resources));

    const recFlatResources = resources => {
        return resources.flatMap(r => {
            if (r.subResources && r.subResources.length) {
                // 特殊处理 frontUrl 为空的情况
                if (!r.frontUrl) {
                    r.frontUrl = (r.subResources[0].frontUrl || '')
                        .split('/')
                        // eslint-disable-next-line no-magic-numbers
                        .slice(0, 3)
                        .join('/');
                }

                return [r, ...recFlatResources(r.subResources)];
            } else {
                return [r];
            }
        });
    };

    memoFlatResourceResult = recFlatResources(cloned);
    return memoFlatResourceResult;
}

export function setupBigDataLog(app, store, router) {
    // console.log('store...', store.state.user);
    const bigDataLog = useBigDataLog();
    const mc = useMessageCenter();

    /// ------ 配置 v-bigdatalog 指令, 用于监控按钮点击操作 --------
    app.use(bigDataLogDirective);

    /// ------ 初始化(配置) 大数据 单例类 --------
    bigDataLog.init({
        moduleName: 'C-NTP',
        childModuleName: '网络货运平台-货主端',
        getUserName: () => store.state.user.user.userName,
        // 从业用户的菜单/按钮资源数据中解析出按路由对应的名字
        routeToNames: route => {
            if (!route) {
                return [];
            }

            /// TODO: 车辆管理页面按钮 大数据统计 待添加，因为 vue3 还在改造中

            const pathNameMap = flatResources(store.state.user.authority).reduce((acc, authItem) => {
                acc[authItem.frontUrl] = authItem.resourceName;
                return acc;
            }, {});

            return route.matched.map(route => pathNameMap[route.path]).filter(n => n);
        },
        // 从业用户的菜单/按钮资源数据中解析出按钮所对应的名字
        resourceIdToNames: resourceId => {
            const pathNameMap = flatResources(store.state.user.authority).reduce((acc, authItem) => {
                acc[authItem.frontUrl || authItem.resourceId] = authItem.resourceName;
                return acc;
            }, {});

            const resourceNames = router.currentRoute.value.matched.map(route => {
                return pathNameMap[route.path];
            });
            // console.log('...', pathNameMap, pathNameMap[resourceId], resourceId);

            return resourceNames.concat([pathNameMap[resourceId] || resourceId]).filter(n => n);
        }
    });

    /// ------ 通过监听 messageCenter, 对路由切换、用户登陆、接口错误等操作进行 监控 --------

    const debouncedBigDataLogOnRouteSwitch = debounce(bigDataLogOnRouteSwitch, 100); // debounce 避免路由切换引起的问题
    mc.observe('/route/beforeSwitch', 'info').by((message, payload) => {
        debouncedBigDataLogOnRouteSwitch(payload.to);
    });

    mc.observe('/auth/logined', 'info').by((message, payload) => {
        // 登陆成功后，进行大数据埋点登记
        useBigDataLog().sendMainMessage();
    });

    observeHttpErrorAndLog(bigDataLog, mc);

    app.config.globalProperties.$report = names => {
        if (names && names.length) {
            bigDataLog.sendDetailMessage({
                model_name1: names[0],
                model_name2: names[1],
                model_name3: names[2],
                model_name4: names[3]
            });
        }
    };
}

/// 监控 Api 接口错误
function observeHttpErrorAndLog(bigDataLog, mc) {
    const httpErrorTypeTraceByBigData = {
        BadResponse: ['BackEndError', '接口出错了'],
        ServerError: ['BackEndError', '系统异常'],
        PageNotFound: ['FrontEndError', '没找到接口信息'],
        NetworkError: ['NetworkError', '网络异常']
    };

    const httpErrorType = {
        BadAuth: '你还没有登录呦！'
    };

    mc.observe('/http', 'error').by((msg, payload) => {
        const bdErrorMsg = httpErrorTypeTraceByBigData[payload];

        if (bdErrorMsg) {
            bigDataLog.sendErrorMessage({
                errType: bdErrorMsg[0],
                errContent: bdErrorMsg[1]
            });
        }

        // 限制 msg 报错的长度
        let _msg = msg;
        const maxMsgLength = 200;
        if ((_msg || '').length > maxMsgLength) {
            _msg = msg.substring(0, maxMsgLength);
        }

        mc.postError(_msg || (bdErrorMsg && bdErrorMsg[1]) || httpErrorType[payload], '/modal');
    });
}
